import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CHECKLIST_STATUS_LABEL,
  type Attachment,
  type ChecklistItem,
  type ChecklistItemStatus,
  type ChecklistPriority,
  type Review,
} from "@docreview/shared";
import { api } from "../lib/api";

const PRIORITY_LABEL: Record<ChecklistPriority, string> = {
  high: "High priority",
  medium: "Medium priority",
  low: "Low priority",
};

const PRIORITY_BADGE: Record<ChecklistPriority, string> = {
  high: "bg-red-100 text-red-800 border-red-200",
  medium: "bg-amber-100 text-amber-900 border-amber-200",
  low: "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_PILL: Record<ChecklistItemStatus, string> = {
  will_address: "bg-emerald-100 text-emerald-800 border-emerald-200",
  not_an_issue: "bg-slate-100 text-slate-700 border-slate-200",
  needs_discussion: "bg-amber-100 text-amber-900 border-amber-200",
};

export interface ChecklistDrawerProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  reviewId: string;
  review: Review;
  attachments: Attachment[];
  onReviewChanged: () => void;
  // False for private deals: a team convention is visible to the whole
  // team, so the backend refuses to create one from a private deal's item.
  // Don't offer the affordance.
  canFlagConventions: boolean;
}

export function ChecklistDrawer({
  open,
  onClose,
  dealId,
  reviewId,
  review,
  attachments,
  onReviewChanged,
  canFlagConventions,
}: ChecklistDrawerProps) {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const submitted = !!review.submittedAt;

  async function refresh() {
    setLoading(true);
    try {
      const { items } = await api.listChecklist(dealId, reviewId);
      setItems(items);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealId, reviewId]);

  // Refresh when new items might have been emitted (parent calls onReviewChanged
  // after streaming finishes — but we also accept being asked to refresh).
  useEffect(() => {
    if (!open) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review.updatedAt]);

  const grouped = useMemo(() => {
    const buckets: Record<ChecklistPriority, ChecklistItem[]> = {
      high: [],
      medium: [],
      low: [],
    };
    for (const it of items) buckets[it.priority].push(it);
    return buckets;
  }, [items]);

  const total = items.length;
  const reviewedCount = items.filter((i) => !!i.status).length;
  const willAddressCount = items.filter((i) => i.status === "will_address").length;
  const revisedDocsCount = attachments.filter((a) => a.kind === "revised_deal_doc").length;

  async function updateItem(id: string, patch: { status?: ChecklistItemStatus | null; comment?: string }) {
    const prev = items;
    setItems((cur) =>
      cur.map((c) =>
        c.id === id
          ? {
              ...c,
              ...(patch.status !== undefined ? { status: patch.status ?? undefined } : {}),
              ...(patch.comment !== undefined ? { comment: patch.comment } : {}),
            }
          : c,
      ),
    );
    try {
      const { item } = await api.updateChecklistItem(dealId, reviewId, id, patch);
      setItems((cur) => cur.map((c) => (c.id === id ? item : c)));
    } catch (e) {
      // Roll back on failure
      setItems(prev);
      window.alert(`Couldn't update: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-30 flex" role="dialog" aria-label="Review checklist">
      <button
        className="flex-1 bg-black/30"
        onClick={onClose}
        aria-label="Close checklist"
      />
      <aside className="w-full max-w-2xl bg-bg flex flex-col h-full shadow-2xl border-l border-slate-200">
        <header className="flex items-start justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="font-semibold">Document checklist</h2>
            <div className="text-xs text-slate-500 mt-0.5">
              {submitted ? (
                <>
                  Submitted by {review.submittedByName ?? review.submittedByEmail} ·{" "}
                  {new Date(review.submittedAt!).toLocaleString()}
                </>
              ) : (
                <>
                  {reviewedCount} / {total} reviewed
                  {total === 0 && " — items appear after a full review is generated"}
                </>
              )}
            </div>
          </div>
          <button className="text-slate-500 hover:text-ink text-xl leading-none" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-6">
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : total === 0 ? (
            <div className="text-sm text-slate-500 italic">
              No checklist items yet. Run the full review in the chat and items will appear here
              automatically.
            </div>
          ) : (
            (["high", "medium", "low"] as ChecklistPriority[]).map((pri) =>
              grouped[pri].length === 0 ? null : (
                <section key={pri}>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`text-xs uppercase tracking-wide px-2 py-0.5 rounded border ${PRIORITY_BADGE[pri]}`}
                    >
                      {PRIORITY_LABEL[pri]}
                    </span>
                    <span className="text-xs text-slate-400">
                      {grouped[pri].filter((i) => i.status).length} / {grouped[pri].length}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {grouped[pri].map((it) => (
                      <ItemCard
                        key={it.id}
                        item={it}
                        disabled={submitted}
                        dealId={dealId}
                        reviewId={reviewId}
                        canFlagConventions={canFlagConventions}
                        onUpdate={(p) => updateItem(it.id, p)}
                        onFlagged={refresh}
                      />
                    ))}
                  </ul>
                </section>
              ),
            )
          )}
        </div>

        <footer className="border-t border-slate-200 p-4 space-y-2">
          {!submitted && (
            <button
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={total === 0 || reviewedCount < total}
              onClick={() => setSubmitOpen(true)}
            >
              {reviewedCount < total
                ? `Submit (${total - reviewedCount} item${total - reviewedCount === 1 ? "" : "s"} left)`
                : "Submit review"}
            </button>
          )}
          {!submitted && willAddressCount > 0 && revisedDocsCount === 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              Reminder: you marked {willAddressCount} item{willAddressCount === 1 ? "" : "s"} as "Will Address". Upload Post-Review Docs in the Documents panel whenever they're ready — you can still submit without them.
            </div>
          )}
          {submitted && (
            <div className="text-xs text-slate-500">
              This review is read-only. The follow-up review has been created on the deal page.
            </div>
          )}
        </footer>
      </aside>

      {submitOpen && (
        <SubmitConfirmModal
          dealId={dealId}
          reviewId={reviewId}
          items={items}
          willAddressCount={willAddressCount}
          revisedDocsCount={revisedDocsCount}
          onClose={() => setSubmitOpen(false)}
          onSubmitted={() => {
            setSubmitOpen(false);
            onReviewChanged();
          }}
        />
      )}
    </div>
  );
}

function ItemCard({
  item,
  disabled,
  dealId,
  reviewId,
  canFlagConventions,
  onUpdate,
  onFlagged,
}: {
  item: ChecklistItem;
  disabled: boolean;
  dealId: string;
  reviewId: string;
  canFlagConventions: boolean;
  onUpdate: (patch: { status?: ChecklistItemStatus | null; comment?: string }) => void;
  onFlagged: () => void;
}) {
  const [comment, setComment] = useState(item.comment ?? "");
  const [editingComment, setEditingComment] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);

  useEffect(() => {
    setComment(item.comment ?? "");
  }, [item.comment]);

  function saveComment() {
    setEditingComment(false);
    if ((item.comment ?? "") !== comment) {
      onUpdate({ comment });
    }
  }

  const alreadyFlagged = !!item.flaggedAsConventionId;

  return (
    <li className="card p-3">
      <div className="text-xs text-slate-400 mb-1 flex items-center justify-between gap-2">
        <span>{item.section}</span>
        {alreadyFlagged && (
          <span
            className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
            title="An attorney flagged this item as a candidate team convention. Awaiting admin review."
          >
            Flagged for admin
          </span>
        )}
      </div>
      <div className="font-medium text-sm">{item.title}</div>
      <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{item.body}</div>
      {item.section_quote && (
        <blockquote className="mt-2 border-l-2 border-slate-300 pl-3 py-0.5 text-sm text-slate-600 italic whitespace-pre-wrap">
          {item.section_quote}
        </blockquote>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {(["will_address", "not_an_issue", "needs_discussion"] as ChecklistItemStatus[]).map((s) => {
          const active = item.status === s;
          return (
            <button
              key={s}
              disabled={disabled}
              className={`text-xs px-2 py-1 rounded border transition ${
                active
                  ? STATUS_PILL[s]
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              } disabled:cursor-not-allowed`}
              onClick={() => onUpdate({ status: active ? null : s })}
              title={active ? "Click to clear" : `Mark "${CHECKLIST_STATUS_LABEL[s]}"`}
            >
              {CHECKLIST_STATUS_LABEL[s]}
            </button>
          );
        })}
      </div>

      {(item.status || item.statusSetByName) && (
        <div className="text-xs text-slate-400 mt-2">
          {item.statusSetByName ?? item.statusSetByEmail ?? "—"}
          {item.statusSetAt && (
            <> · {new Date(item.statusSetAt).toLocaleString()}</>
          )}
        </div>
      )}

      {!disabled ? (
        editingComment ? (
          <div className="mt-2">
            <textarea
              className="input w-full text-sm"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              autoFocus
              placeholder="Optional comment…"
            />
            <div className="flex justify-end gap-2 mt-1">
              <button
                className="text-xs text-slate-500 hover:text-ink"
                onClick={() => {
                  setComment(item.comment ?? "");
                  setEditingComment(false);
                }}
              >
                Cancel
              </button>
              <button className="text-xs text-ink font-medium hover:underline" onClick={saveComment}>
                Save
              </button>
            </div>
          </div>
        ) : item.comment ? (
          <button
            className="mt-2 text-left text-sm text-slate-700 italic w-full hover:bg-slate-50 rounded p-1 -m-1"
            onClick={() => setEditingComment(true)}
            title="Click to edit"
          >
            "{item.comment}"
          </button>
        ) : (
          <button
            className="mt-2 text-xs text-slate-400 hover:text-ink"
            onClick={() => setEditingComment(true)}
          >
            + add comment
          </button>
        )
      ) : item.comment ? (
        <div className="mt-2 text-sm text-slate-700 italic">"{item.comment}"</div>
      ) : null}

      {!disabled && !alreadyFlagged && canFlagConventions && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <button
            className="text-xs text-indigo-700 hover:text-indigo-900 hover:underline"
            onClick={() => setFlagOpen(true)}
            title="Tell the admin this finding represents a recurring team-doc quirk Claude should learn to recognize"
          >
            Flag as known team convention →
          </button>
        </div>
      )}

      {flagOpen && (
        <FlagConventionModal
          dealId={dealId}
          reviewId={reviewId}
          item={item}
          onClose={() => setFlagOpen(false)}
          onFlagged={() => {
            setFlagOpen(false);
            onFlagged();
          }}
        />
      )}
    </li>
  );
}

function FlagConventionModal({
  dealId,
  reviewId,
  item,
  onClose,
  onFlagged,
}: {
  dealId: string;
  reviewId: string;
  item: ChecklistItem;
  onClose: () => void;
  onFlagged: () => void;
}) {
  const [reason, setReason] = useState("");
  const [title, setTitle] = useState(item.title.slice(0, 160));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit = reason.trim().length >= 10 && title.trim().length > 0 && !busy;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await api.flagItemAsConvention(dealId, reviewId, item.id, {
        reason: reason.trim(),
        title: title.trim(),
      });
      onFlagged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="card w-full max-w-lg p-6">
        <h2 className="font-semibold text-lg mb-2">Flag as known team convention</h2>
        <p className="text-sm text-slate-600 mb-4">
          Tell the admin why this finding represents a recurring quirk of the team's documents
          (not a real issue). Be specific — the rule should only match this exact pattern, not
          a general category of findings. Submitting will mark this item as <strong>Not An
          Issue</strong> and send your suggestion for admin review.
        </p>
        <label className="block text-sm font-medium mb-1">Convention title</label>
        <input
          className="input mb-3"
          maxLength={160}
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 160))}
          placeholder="Short label (e.g., 'TS principal excludes the TEA')"
        />
        <label className="block text-sm font-medium mb-1">
          Specific rationale <span className="text-slate-400 font-normal">(min ~10 chars)</span>
        </label>
        <textarea
          className="input resize-none mb-2"
          rows={5}
          maxLength={2000}
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 2000))}
          placeholder="Describe the exact pattern that should trigger this convention. Example: 'If the math gap between the TS principal and the Note principal equals the Transaction Expense Amount listed in the closing flow, this is the team's standard convention of excluding the TEA from the TS headline figure. It is not a real discrepancy.'"
        />
        <div className="text-right text-xs text-slate-400 mb-3">{reason.length} / 2000</div>
        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" disabled={!canSubmit} onClick={submit}>
            {busy ? "Sending…" : "Send to admin"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubmitConfirmModal({
  dealId,
  reviewId,
  items,
  willAddressCount,
  revisedDocsCount,
  onClose,
  onSubmitted,
}: {
  dealId: string;
  reviewId: string;
  items: ChecklistItem[];
  willAddressCount: number;
  revisedDocsCount: number;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const nav = useNavigate();
  // Surfaced as a soft reminder only — the backend no longer requires
  // Post-Review Docs before submitting.
  const docsReminder = willAddressCount > 0 && revisedDocsCount === 0;
  const notAnIssueCount = items.filter((i) => i.status === "not_an_issue").length;
  const needsDiscussionCount = items.filter((i) => i.status === "needs_discussion").length;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const { nextReview } = await api.submitChecklist(dealId, reviewId);
      onSubmitted();
      // Land back on the deal page so the team sees the new follow-up review.
      nav(`/deals/${dealId}`);
      void nextReview;
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="card w-full max-w-md p-6">
        <h2 className="font-semibold text-lg mb-3">Submit review?</h2>
        <p className="text-sm text-slate-600 mb-3">
          The team will be notified, the checklist will become read-only, and a follow-up review
          will be created for the team's next pass.
        </p>
        <ul className="text-sm text-slate-700 mb-3 space-y-0.5">
          <li>{willAddressCount} marked Will Address</li>
          <li>{notAnIssueCount} marked Not An Issue</li>
          <li>{needsDiscussionCount} flagged for group discussion</li>
        </ul>
        {docsReminder && (
          <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3 mb-3">
            Heads up: you marked items as "Will Address" but haven't uploaded any Post-Review
            Docs yet. You can still submit — just remember to upload them under "Post-Review
            Docs" in the Documents panel whenever they're ready.
          </div>
        )}
        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy}
            onClick={submit}
          >
            {busy ? "Submitting…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
